import { Component, OnInit } from '@angular/core';
import { CardModule } from 'primeng/card';
import { TranslationPipe } from '../../../pipes/translation.pipe';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { DateTime } from 'luxon';
import { ScrollPanelModule } from 'primeng/scrollpanel';
import { ButtonModule } from 'primeng/button';
import { DashboardTitleService } from '../../../services/dashboard-title.service';
import { TabsModule } from 'primeng/tabs';
import { DialogService, DynamicDialogModule, DynamicDialogRef } from 'primeng/dynamicdialog';
import {
  CustomerClient, CustomerDto,
  DocumentClient,
  DocumentShortInfoDto,
  Gender,
  PetBehavior,
  PetClient,
  PetDto,
  TreatmentClient,
  TreatmentDto
} from '../../../api/api-client';
import { RippleModule } from 'primeng/ripple';
import { AddDocumentDialogComponent } from '../../dialogs/add-document-dialog/add-document-dialog.component';
import { ViewDocumentDialogComponent } from '../../dialogs/view-document-dialog/view-document-dialog.component';
import { SafeHtmlPipe } from '../../../pipes/safe-html.pipe';
import { FeedbackDataService } from '../../../services/feedback-data.service';
import { FeedbackData } from '../../../models/feedback-data';
import { DeleteDocumentDialogComponent } from '../../dialogs/delete-document-dialog/delete-document-dialog.component';
import { PetDetailsDialogComponent } from './pet-details-dialog/pet-details-dialog.component';
import { CardWithHeaderComponent } from '../../widgets/card-with-header/card-with-header.component';
import { CardWithTabsComponent } from '../../widgets/card-with-tabs/card-with-tabs.component';
import { GreedyScrollPanelComponent } from '../../widgets/greedy-scroll-panel/greedy-scroll-panel.component';
import { AttachmentGalleryComponent } from '../../widgets/attachment-gallery/attachment-gallery.component';
import { ViewDocumentRoutingInfo } from '../../../models/view-document-routing-info';
import { SubscriptionManager } from '../../../services/subscription-manager.service';

@Component({
  selector: 'app-pet-detail',
  imports: [
    CardModule,
    ButtonModule,
    TabsModule,
    TranslationPipe,
    CommonModule,
    ScrollPanelModule,
    RippleModule,
    DynamicDialogModule,
    SafeHtmlPipe,
    CommonModule,
    RouterModule,
    CardWithHeaderComponent,
    CardWithTabsComponent,
    GreedyScrollPanelComponent,
    AttachmentGalleryComponent
  ],
  providers: [SubscriptionManager],
  templateUrl: './pet-detail.component.html',
  styleUrl: './pet-detail.component.scss'
})
export class PetDetailComponent implements OnInit {

  petId!: string;
  pet!: PetDto | undefined;
  owner!: CustomerDto | undefined;
  treatmentsOfPet: TreatmentDto[] = [];
  selectedBehaviourAnamnesisTab = 'left';
  selectedImagesDocumentTab = 'left';

  refAddDocumentDialog!: DynamicDialogRef<AddDocumentDialogComponent> | null;
  refViewDocumentDialog!: DynamicDialogRef<ViewDocumentDialogComponent> | null;
  refDeleteDocumentDialog!: DynamicDialogRef<DeleteDocumentDialogComponent> | null;
  refPetDetailsDialog!: DynamicDialogRef<PetDetailsDialogComponent> | null;

  plannedTreatments: TreatmentDto[] = [];
  pastTreatments: TreatmentDto[] = [];

  pinnedImages: DocumentShortInfoDto[] = [];
  notPinnedImages: DocumentShortInfoDto[] = [];
  pinnedDocuments: DocumentShortInfoDto[] = [];
  notPinnedDocuments: DocumentShortInfoDto[] = [];

  constructor(
    private readonly subscriptionManager: SubscriptionManager,
    private readonly petService: PetClient,
    private readonly customerService: CustomerClient,
    private readonly treatmentService: TreatmentClient,
    private readonly route: ActivatedRoute,
    private readonly translationPipe: TranslationPipe,
    private readonly dashboardService: DashboardTitleService,
    private readonly dialogService: DialogService,
    private readonly documentService: DocumentClient,
    private readonly router: Router,
    private readonly feedbackService: FeedbackDataService) {

  }

  ngOnInit(): void {
    this.subscriptionManager.call(
      this.route.paramMap,
      (paramMap => {
        this.petId = paramMap.get('petId') ?? '';
      })
    );
    this.subscriptionManager.call(
      this.petService.getPetById(this.petId),
      (x => {
        this.pet = x.result;
        if (this.pet?.ownerId) {
          this.feedbackService.feedbackData = new FeedbackData();
          this.feedbackService.feedbackData.petId = this.petId;
          this.feedbackService.feedbackData.customerId = this.pet.ownerId;
          this.subscriptionManager.call(
            this.customerService.getCustomerById(this.pet.ownerId),
            (y => {
              this.owner = y.result;
              this.dashboardService.breadCrumbData.next({
                petName: this.pet?.nickname ?? '',
                petId: this.pet?.id ?? '',
                customerFirstname: this.owner?.firstName ?? '',
                customerLastname: this.owner?.lastName ?? '',
                customerId: this.owner?.id ?? '',
                treatmentDate: DateTime.now(),
                treatmentId: '',
                treatmentTitle:""
              });
            })
          );
        }
        this.subscriptionManager.call(
          this.treatmentService.getBatchTreatments(this.pet.treatmentIds),
          (z => {
              this.treatmentsOfPet = z.result;
              this.plannedTreatments = this.getPlannedTreatments();
              this.pastTreatments = this.getPastTreatments();
              this.treatmentsOfPet = this.treatmentsOfPet.toSorted((a: TreatmentDto, b: TreatmentDto) => this.compareLuxonDates(b.appointmentDate, a.appointmentDate));
            }
          )
        );

        this.subscriptionManager.call(
          this.documentService.getDocumentShortInfosForPet(this.petId),
          (x => {
            this.pinnedImages = x.result.filter(y => y.documentType === 'Image' && y.isPinned);
            this.notPinnedImages = x.result.filter(y => y.documentType === 'Image' && !y.isPinned);
            this.pinnedDocuments = x.result.filter(y => y.documentType === 'Document' && y.isPinned);
            this.notPinnedDocuments = x.result.filter(y => y.documentType === 'Document' && !y.isPinned);
          })
        );
      })
    );
  }

  compareLuxonDates(a: DateTime, b: DateTime) {
    return a.toMillis() - b.toMillis();
  }

  getDateAndTime(date: DateTime | undefined): string | undefined {
    if (date) {
      return date.toLocaleString({
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
    return undefined;
  }

  getDate(date: DateTime | undefined): string | undefined {
    if (date) {
      return date.toLocaleString({ day: '2-digit', month: '2-digit', year: 'numeric' });
    }
    return undefined;
  }

  getTime(date: DateTime | undefined) {
    if (date) {
      return date.toLocaleString(DateTime.TIME_24_SIMPLE) + ' ' + this.translationPipe.transform('TimeAppendix');
    }
    return '';
  }

  estimateHighDangerClassActive(pet: PetDto | undefined): string {
    if (pet) {
      if (pet.behavior == 'HighDanger') {
        return 'border-2 border-(--p-text-color)';
      }
      return 'saturate-[30%]';
    }
    return '';
  }

  estimateMediumDangerClassActive(pet: PetDto | undefined): string {
    if (pet) {
      if (pet.behavior == 'MediumDanger') {
        return 'border-2 border-(--p-text-color)';
      }
      return 'saturate-[30%]';
    }
    return '';
  }

  estimateLowDangerClassActive(pet: PetDto | undefined): string {
    if (pet) {
      if (pet.behavior == 'LowDanger') {
        return 'border-2 border-(--p-text-color)';
      }
      return 'saturate-[30%]';
    }
    return '';
  }

  estimatePerfectlySafeClassActive(pet: PetDto | undefined): string {
    if (pet) {
      if (pet.behavior == 'PerfectlySafe') {
        return 'border-2 border-(--p-text-color)';
      }
      return 'saturate-[30%]';
    }
    return '';
  }

  getAge(dateOfBirth: DateTime | undefined) {
    if (dateOfBirth) {
      const age = DateTime.now().diff(dateOfBirth);
      if (age.as('months') < 0) {
        return age.as('days').toFixed() + ' ' + this.translationPipe.transform('Date.Days');
      }
      if (age.as('years') < 2) {
        return age.as('months').toFixed() + ' ' + this.translationPipe.transform('Date.Months');
      } else {
        return age.as('years').toFixed() + ' ' + this.translationPipe.transform('Date.Years');
      }
    }
    return '';
  }

  getGender(gender: Gender | undefined) {
    if (gender) {
      return this.translationPipe.transform('PetDetails.Gender.' + gender);
    }
    return '';
  }

  getPlannedTreatments(): TreatmentDto[] {
    if (this.treatmentsOfPet) {
      if (this.treatmentsOfPet.length == 0) {
        return [];
      }
      return this.treatmentsOfPet
        ?.filter(x => x.appointmentDate > DateTime.now())
        ?.sort((a, b) => a.appointmentDate.diffNow().as('minutes') - b.appointmentDate.diffNow().as('minutes'));
    }
    return [];
  }

  getPastTreatments(): TreatmentDto[] {
    if (this.treatmentsOfPet) {
      if (this.treatmentsOfPet.length == 0) {
        return [];
      }
      return this.treatmentsOfPet
        ?.filter(x => x.appointmentDate < DateTime.now())
        ?.sort((a, b) => b.appointmentDate.diffNow().as('minutes') - a.appointmentDate.diffNow().as('minutes'));
    }
    return [];
  }

  openAddDocumentDialog(docType: string, isSkeleton = false) {
    this.refAddDocumentDialog = this.dialogService.open(AddDocumentDialogComponent, {
      modal: true,
      closeOnEscape: true,
      closable: true,
      data: {
        sourcePage: 'pet-detail',
        documentType: docType,
        entityId: this.petId,
        isSkeleton: isSkeleton
      }
    });
    this.subscriptionManager.call(
      this.refAddDocumentDialog?.onClose,
      (x => {
        this.ngOnInit();
      })
    );
  }

  openViewSkeletonDialog() {
    this.refViewDocumentDialog = this.dialogService.open(ViewDocumentDialogComponent, {
      modal: true,
      closeOnEscape: true,
      closable: true,
      data: {
        skeletonAnnotation: this.pet?.skeletonAnnotation,
        species: this.pet?.species,
        isSkeleton: true
      } as ViewDocumentRoutingInfo
    });
  }

  openViewDocumentDialog(documentId: string) {
    this.subscriptionManager.call(
      this.documentService.getDocumentById(documentId),
      (x => {
        this.refViewDocumentDialog = this.dialogService.open(ViewDocumentDialogComponent, {
          modal: true,
          closeOnEscape: true,
          closable: true,
          header: x.result.filename,
          data: {
            isSkeleton: false,
            document: x.result
          } as ViewDocumentRoutingInfo
        });
      })
    );
  }

  unpinDocument(document: DocumentShortInfoDto) {
    this.subscriptionManager.call(
      this.documentService.unpinDocument(document.id),
      (x => {
        let i = this.pinnedImages.findIndex(x => x.id == document.id);
        if (i != -1) {
          this.pinnedImages[i].isPinned = false;
          this.notPinnedImages.push(this.pinnedImages[i]);
          this.pinnedImages.splice(i, 1);
          return;
        }
        i = this.pinnedDocuments.findIndex(x => x.id == document.id);
        if (i != -1) {
          this.pinnedDocuments[i].isPinned = false;
          this.notPinnedDocuments.push(this.pinnedDocuments[i]);
          this.pinnedDocuments.splice(i, 1);
        }
      })
    );
  }

  pinDocument(document: DocumentShortInfoDto) {
    this.subscriptionManager.call(
      this.documentService.pinDocument(document.id),
      (x => {
        let i = this.notPinnedImages.findIndex(x => x.id == document.id);
        if (i != -1) {
          this.notPinnedImages[i].isPinned = true;
          this.pinnedImages.push(this.notPinnedImages[i]);
          this.notPinnedImages.splice(i, 1);
          return;
        }
        i = this.notPinnedDocuments.findIndex(x => x.id == document.id);
        if (i != -1) {
          this.notPinnedDocuments[i].isPinned = true;
          this.pinnedDocuments.push(this.notPinnedDocuments[i]);
          this.notPinnedDocuments.splice(i, 1);
        }
      })
    );
  }

  openEditSkeletonPage() {
    this.router.navigate(['../loggedin/images/annotate'], {
      state: {
        routingData: {
          sourcePage: 'pet-detail',
          entityId: this.petId,
          isSkeleton: true,
          species: this.pet?.species,
          skeletonAnnotation: this.pet?.skeletonAnnotation
        }
      }
    });

  }

  openEditDocumentPage(document: DocumentShortInfoDto) {
    this.subscriptionManager.call(
      this.documentService.getDocumentById(document.id),
      (x => {
        this.router.navigate(['../loggedin/images/annotate'], {
          state: {
            routingData: {
              sourcePage: 'pet-detail',
              documentType: x.result.documentType,
              entityId: this.petId
            },
            addedDocument: x.result
          }
        });
      })
    );

  }

  protected openDeleteDocumentDialog(document: DocumentShortInfoDto) {
    this.refDeleteDocumentDialog = this.dialogService.open(DeleteDocumentDialogComponent, {
      modal: true,
      closeOnEscape: true,
      closable: true,
      data: {
        entityId: document.id,
        filename: document.filename,
        documentType: document.documentType
      }
    });
    this.subscriptionManager.call(
      this.refDeleteDocumentDialog?.onClose,
      (x => {
        if (x) {
          this.pinnedDocuments = this.pinnedDocuments.filter(x => x.id != document.id);
          this.notPinnedDocuments = this.notPinnedDocuments.filter(x => x.id != document.id);
          this.pinnedImages = this.pinnedImages.filter(x => x.id != document.id);
          this.notPinnedImages = this.notPinnedImages.filter(x => x.id != document.id);
        }
      })
    );
  }


  protected openTreatmentDetails(treatmentId: string) {
    this.router.navigate(['../loggedin/treatment-detail/' + treatmentId]);
  }

  protected openEditPetPage() {
    this.router.navigate(['../loggedin/pets/edit/' + this.petId]);
  }

  protected openAddTreatmentPage() {
    this.router.navigate(['../loggedin/treatments/create'], {
      queryParams: {
        petId: this.pet?.id,
        ownerId: this.pet?.ownerId
      }
    });
  }

  protected GetBehaviorText(behavior: PetBehavior) {
    return `Pet.Behavior.${behavior}`;
  }

  protected openPetDetailWidget(pet: PetDto | undefined) {
    this.refPetDetailsDialog = this.dialogService.open(PetDetailsDialogComponent, {
        modal: true,
        closeOnEscape: true,
        closable: true,
        data: this.pet
      }
    );
  }

  protected hasSkeletonAnnotation(pet: PetDto) {
    return pet.skeletonAnnotation?.annotationElements?.length && pet.skeletonAnnotation?.annotationElements?.length > 0;
  }
}
